import { useApiQuery } from '../../engine/api/hooks';
import { Badge, Skeleton } from '../../engine/design/primitives/Misc';
import { Icon } from '../../engine/design/icons';
import { formatMoney, formatMinutes } from '../../lib/format';

// The commercials strip (docs/servicedesk-v2-proposal.md P7): what covers this work, what it earns
// and what it has cost — beside the ticket, before the work, not at month end. Every figure comes
// from GET /v1/tickets/:id/commercials, which reads the finance core; nothing is computed here.

export interface Commercials {
  currency: string | null;
  billable: boolean;
  contract: { id: string; ref: string | null; status: string; cycle: string; currency: string; period_charge_minor: string; end_date: string | null } | null;
  candidate_contracts: Array<{ id: string; ref: string | null }>;
  rates: { technician_id: string | null; resolved_for: string; cost_rate_minor: string | null; bill_rate_minor: string | null; source: string };
  rounding: { round_to_minutes: number; minimum_minutes: number };
  prepay: Array<{ prepay_account_id: string; unit: string; currency: string; purchased_qty: string; consumed_qty: string; remaining_qty: string; over_drawn: boolean }>;
  effort: { minutes: number; entries: number; invoiced_minutes: number; uninvoiced_minutes: number; cost_minor: string; billable_amount_minor: string; margin_minor: string };
  warnings: Array<{ code: string; severity: 'info' | 'warn' | 'critical'; message: string }>;
}

const SEVERITY_TONE = { info: 'muted', warn: 'warn', critical: 'neg' } as const;

export function useCommercials(ticketId: string, enabled = true) {
  return useApiQuery<Commercials>(['ticket-commercials', ticketId], `/v1/tickets/${ticketId}/commercials`, { enabled: enabled && Boolean(ticketId) });
}

export function CommercialsPanel({ ticketId }: { ticketId: string }) {
  const q = useCommercials(ticketId);
  if (q.isLoading) return <Skeleton lines={5} />;
  const c = q.data;
  if (!c) return <span className="hf-muted">No commercial detail for this ticket.</span>;
  const currency = c.currency ?? 'USD';

  return (
    <div className="hf-stack" style={{ gap: 'var(--s-3)' }}>
      {c.warnings.length > 0 && (
        <ul className="hf-stack" style={{ gap: 'var(--s-1)', listStyle: 'none', padding: 0, margin: 0 }} aria-label="Commercial warnings">
          {c.warnings.map((w) => (
            <li key={w.code} className="hf-row" style={{ gap: 'var(--s-1)', alignItems: 'flex-start' }}>
              <Badge tone={SEVERITY_TONE[w.severity]} dot>{w.severity === 'critical' ? 'Stop' : w.severity === 'warn' ? 'Check' : 'Note'}</Badge>
              <span style={{ fontSize: 'var(--t-12)' }}>{w.message}</span>
            </li>
          ))}
        </ul>
      )}

      <dl className="hf-kv">
        <div>
          <dt>Coverage</dt>
          <dd>
            {c.contract ? (
              <span className="hf-row" style={{ gap: 'var(--s-1)' }}>
                <Icon name="file-text" size={12} />
                {c.contract.ref ?? 'Contract'}
                <Badge tone={c.contract.status === 'active' ? 'pos' : 'muted'} dot={false}>{c.contract.status}</Badge>
              </span>
            ) : (
              <span className="hf-muted">Time and materials</span>
            )}
          </dd>
        </div>
        <div>
          <dt>Charge rate</dt>
          <dd className="hf-num">{formatMoney(c.rates.bill_rate_minor, currency)}/h <span className="hf-muted">cost {formatMoney(c.rates.cost_rate_minor, currency)}/h</span></dd>
        </div>
        <div>
          <dt>Rounding</dt>
          <dd className="hf-num">
            {c.rounding.round_to_minutes === 1 ? 'Exact' : `${c.rounding.round_to_minutes} min blocks`}
            {c.rounding.minimum_minutes > 0 && <span className="hf-muted"> · min {c.rounding.minimum_minutes}m</span>}
          </dd>
        </div>
        {c.prepay.map((p) => (
          <div key={p.prepay_account_id}>
            <dt>Prepaid {p.unit}</dt>
            <dd className="hf-num">
              <Badge tone={p.over_drawn ? 'neg' : Number(p.remaining_qty) <= 1 ? 'warn' : 'pos'}>{p.remaining_qty} left</Badge>
              <span className="hf-muted"> of {p.purchased_qty}</span>
            </dd>
          </div>
        ))}
        <div>
          <dt>Effort so far</dt>
          <dd className="hf-num">{formatMinutes(c.effort.minutes)} <span className="hf-muted">over {c.effort.entries} {c.effort.entries === 1 ? 'entry' : 'entries'}</span></dd>
        </div>
        <div>
          <dt>Charged</dt>
          <dd className="hf-num">{formatMoney(c.effort.billable_amount_minor, currency)} <span className="hf-muted">cost {formatMoney(c.effort.cost_minor, currency)}</span></dd>
        </div>
        <div>
          <dt>Margin</dt>
          <dd className="hf-num">
            <Badge tone={BigInt(c.effort.margin_minor) >= 0n ? 'pos' : 'neg'} dot={false}>{formatMoney(c.effort.margin_minor, currency)}</Badge>
          </dd>
        </div>
        {c.effort.uninvoiced_minutes > 0 && (
          <div>
            <dt>Not yet invoiced</dt>
            <dd className="hf-num">{formatMinutes(c.effort.uninvoiced_minutes)}</dd>
          </div>
        )}
      </dl>
    </div>
  );
}
